import React from "react";
import { Plus, Trash2, AlertCircle } from "lucide-react";
import { UnitToggle } from "../../../components/common/UnitToggle";
import { WeightInput } from "../../../components/common/WeightInput";
import type { WeightUnit } from "../../../hooks/useWeightUnit";
import { AutocompleteSelect } from "../../../components/common/AutocompleteSelect";
import { useLanguage } from "../../../features/language/useLanguage";

interface LotRow {
  lotId: string;
  remaining: number;
  usedWeight: number | undefined;
  stockBefore?: number;
  stockAfter?: number;
  originalUsedWeight?: number;
}

interface Props {
  lots: LotRow[];
  lotOptions: {
    id: string;
    name: string;
    remaining: number;
    density?: number | null;
    moisture?: number | null;
    processType?: string | null;
    hasGreenBeanProfile?: boolean;
    greenBeanProfileId?: string | null;
  }[];
  onLotChange: (index: number, lotId: string) => void;
  onWeightChange: (index: number, value: number | undefined) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
  totalUsedWeight?: number;
  roastInputWeight?: number;
  showMismatchWarning?: boolean;
  hideStockInfo?: boolean;
  disableUseWeight?: boolean;
  unit?: WeightUnit;
  onToggle?: (unit: WeightUnit) => void;
  readOnly?: boolean;
}

export const LotSection: React.FC<Props> = React.memo(({
  lots,
  lotOptions,
  onLotChange,
  onWeightChange,
  onAdd,
  onRemove,
  totalUsedWeight,
  roastInputWeight,
  showMismatchWarning,
  hideStockInfo,
  disableUseWeight,
  unit = 'kg',
  onToggle,
  readOnly = false,
}) => {
  const { t } = useLanguage();
  const formatWeight = (val: number | undefined) => {
    if (val === undefined || isNaN(val)) return t('settingsMcp.values.none');
    if (unit === 'kg') return val.toFixed(3);
    return String(Math.round(val * 1000));
  };

  return (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-md font-medium text-gray-900">{t('roast.lotSection.lotsUsed')}</h3>
        <div className="flex items-center gap-2">
          {onToggle && <UnitToggle unit={unit} onToggle={onToggle} />}
          {!disableUseWeight && !readOnly && (
            <button
              type="button"
              onClick={onAdd}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded text-primary-700 bg-primary-100 hover:bg-primary-200"
            >
              <Plus className="h-4 w-4 mr-1" />
              {t('roast.lotSection.addLot')}
            </button>
          )}
        </div>
      </div>

      {typeof totalUsedWeight === "number" && totalUsedWeight > 0 && (
        <div
          className={`relative mb-4 rounded-md border px-3 py-2 ${
            showMismatchWarning ? "border-red-400" : "border-gray-200 bg-gray-100"
          }`}
        >
          {showMismatchWarning && (
            <div className="absolute inset-0 rounded-md bg-red-200 animate-pulse pointer-events-none" />
          )}

          <div className="relative z-10">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium text-gray-700">
                {t('roast.lotSection.totalUsedWeight', { unit })}
              </span>
              <span className="text-sm font-mono text-gray-900">
                {formatWeight(totalUsedWeight)}
              </span>
            </div>

            {showMismatchWarning && (
              <p className="mt-1 text-xs text-red-600">
                {t('roast.lotSection.totalUsedWeightMismatch')}
                {typeof roastInputWeight === "number" && roastInputWeight > 0
                  ? ` (${formatWeight(roastInputWeight)} ${unit}).`
                  : "."}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {lots.map((row, index) => {
          // Filter available options for this row
          const otherSelectedIds = lots
            .filter((_, idx) => idx !== index)
            .map((l) => l.lotId)
            .filter((id) => id !== "");

          const rowOptions = lotOptions.filter(
            (l) => !otherSelectedIds.includes(l.id)
          );

          const val = row.usedWeight;
          const isOverLimit = val !== undefined && val > row.remaining;
          const selectedLot = lotOptions.find(l => l.id === row.lotId);

          return (
            <div
              key={index}
              className="flex gap-x-4 gap-y-1 sm:gap-y-4 items-start border p-4 rounded-md bg-white shadow-sm flex-wrap"
            >
              <div className="flex-[2] min-w-[200px]">
                <div className="min-h-[2.5rem] flex items-end mb-0.5 sm:mb-1">
                  <label className="block text-sm font-medium text-gray-700" data-test-id="label-lot-number">
                    {t('inventory.history.lotNumber')}
                  </label>
                </div>
                <AutocompleteSelect
                  value={row.lotId}
                  onChange={(val) => onLotChange(index, val)}
                  options={rowOptions.map(l => ({
                    id: l.id,
                    label: l.name,
                    text: l.name,
                    value: l.id,
                  }))}
                  placeholder={t('roast.lotSection.selectLot')}
                  disabled={disableUseWeight || readOnly}
                  className="mt-1"
                  allowCustom={false}
                  inputClassName={readOnly ? "py-1.5 pl-3 pr-10 truncate bg-gray-50 cursor-not-allowed" : "py-1.5 pl-3 pr-10 truncate"}
                  wrapperClassName="rounded-md"
                  optionsClassName="min-w-full w-max max-w-[28rem]"
                  optionClassName="whitespace-nowrap"
                />
              </div>

              <div className="flex-1 min-w-[120px]">
                <div className="min-h-[2.5rem] flex items-end mb-0.5 sm:mb-1">
                  <label className="block text-sm font-medium text-gray-700" data-test-id="label-process-type">
                    {t('roast.lotSection.processType')}
                  </label>
                </div>
                <div className="bg-gray-50 border rounded-md p-1.5 text-sm">
                  {selectedLot?.processType || t('settingsMcp.values.none')}
                </div>
              </div>

              <div className="flex-1 min-w-[120px]">
                <div className="min-h-[2.5rem] flex items-end mb-0.5 sm:mb-1">
                  <label className="block text-sm font-medium text-gray-700" data-test-id="label-density">
                    {t('roast.lotSection.density')}
                  </label>
                </div>
                <div className="bg-gray-50 border rounded-md p-1.5 text-sm">
                  {selectedLot?.density ?? t('settingsMcp.values.none')}
                </div>
              </div>

              <div className="flex-1 min-w-[120px]">
                <div className="min-h-[2.5rem] flex items-end mb-0.5 sm:mb-1">
                  <label className="block text-sm font-medium text-gray-700" data-test-id="label-moisture-content">
                    {t('greenBean.quality.moistureContent')}
                  </label>
                </div>
                <div className="bg-gray-50 border rounded-md p-1.5 text-sm">
                  {selectedLot?.moisture ? `${selectedLot.moisture}%` : t('settingsMcp.values.none')}
                </div>
              </div>

              {!hideStockInfo && (
                <div className="flex-1 min-w-[120px]">
                  <div className="min-h-[2.5rem] flex items-end mb-0.5 sm:mb-1">
                    <label className="block text-sm font-medium text-gray-700" data-test-id="label-remaining">
                      {t('salesOrder.items.remaining')}
                    </label>
                  </div>
                  <div className="bg-gray-50 border rounded-md p-1.5 text-sm">
                    {formatWeight(row.remaining)}
                  </div>
                  {(row.stockBefore !== undefined || row.stockAfter !== undefined) && (
                    <div className="mt-1 text-xs text-gray-600 space-y-0.5">
                      {row.stockBefore !== undefined && (
                        <div>{t('roast.lotSection.before')}: {formatWeight(row.stockBefore)}</div>
                      )}
                      {row.stockAfter !== undefined && (
                        <div>{t('roast.lotSection.after')}: {formatWeight(row.stockAfter)}</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex-1 min-w-[120px]">
                <div className="min-h-[2.5rem] flex items-end mb-0.5 sm:mb-1">
                  <label className="block text-sm font-medium text-gray-700" data-test-id="label-use-weight">
                    {t('roast.lotSection.useWeight')}
                  </label>
                </div>
                <div className="relative">
                  <WeightInput
                    value={val}
                    onChange={(newVal) => onWeightChange(index, newVal)}
                    unit={unit}
                    className={`block w-full rounded-md sm:text-sm border p-1.5 ${
                      isOverLimit
                        ? "border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                    }`}
                    placeholder={t('inventory.history.placeholderKg')}
                    disabled={disableUseWeight || readOnly}
                    {...(index === 0 ? { 'data-test-id': 'wo-lot-used-0' } : {})}
                  />
                  {isOverLimit && !hideStockInfo && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <AlertCircle
                        className="h-4 w-4 text-red-500"
                        aria-hidden="true"
                      />
                    </div>
                  )}
                </div>
                {isOverLimit && !hideStockInfo && (
                  <p className="mt-1 text-xs text-red-600">
                    {t('roast.lotSection.exceedsRemainingStock')}
                  </p>
                )}
              </div>

              {!hideStockInfo && (
                <div className="flex-1 min-w-[120px]">
                  <div className="min-h-[2.5rem] flex items-end mb-0.5 sm:mb-1">
                    <label className="block text-sm font-medium text-gray-700" data-test-id="label-after-use">
                      {t('roast.lotSection.afterUse')}
                    </label>
                  </div>
                  <div className="bg-gray-50 border rounded-md p-1.5 text-sm">
                    {formatWeight(row.remaining - (val || 0))}
                  </div>
                </div>
              )}

              {!readOnly && (
                <button
                  type="button"
                  onClick={() => onRemove(index)}
                  className="sm:mt-11 mt-[42px] text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={disableUseWeight}
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});
